import { CheckCircle2, Salad } from "lucide-react";
import { formatNumber } from "../../lib/format";
import type { LoggingFlowReviewItem } from "../../lib/logging-flow";
import { buildReviewFlowItems, type MealEstimateData } from "./card-data";
import { CardActionButton, ChatCardShell, MacroChip } from "./card-ui";
import type { MealEstimateLogState } from "./meal-estimate-log";

const PREVIEW_ITEM_LIMIT = 4;

export interface MealEstimateCardViewProps {
  readonly data: MealEstimateData;
  readonly logState: MealEstimateLogState;
  readonly onLog: () => void;
  readonly onStartReview?: (items: readonly LoggingFlowReviewItem[]) => void;
  readonly onSendMessage?: (text: string) => void;
}

function LoggedConfirmation({ data }: { readonly data: MealEstimateData }) {
  const calories = data.variant === "memory" ? data.memoryMatch?.calories : data.totals?.calories;
  return (
    <div className="flex min-h-[44px] items-center justify-between gap-3 rounded-xl bg-success/10 px-3 py-2 text-success" data-testid="meal-estimate-logged">
      <span className="flex items-center gap-2 text-sm font-semibold">
        <CheckCircle2 size={18} aria-hidden="true" /> Logged
      </span>
      <span className="text-sm font-semibold">
        {calories === undefined ? "Calories unknown" : `${formatNumber(calories)} kcal`}
      </span>
    </div>
  );
}

function LogActions(props: MealEstimateCardViewProps) {
  const { data, logState, onLog, onStartReview, onSendMessage } = props;
  const busy = logState.kind === "logging" || logState.kind === "logged";
  return (
    <div className="flex flex-col gap-2">
      {logState.kind === "logged" && <LoggedConfirmation data={data} />}
      {data.variant === "items" && onStartReview !== undefined && (
        <CardActionButton label="Review" testId="meal-estimate-review-btn" variant="primary" disabled={busy} onPress={() => onStartReview(buildReviewFlowItems(data))} />
      )}
      <CardActionButton
        label={logState.kind === "logging" ? "Logging…" : logState.kind === "logged" ? "Logged" : logState.kind === "error" ? "Retry" : "Log"}
        testId="meal-estimate-log-btn"
        variant={data.variant === "memory" ? "primary" : "secondary"}
        disabled={busy}
        onPress={onLog}
      />
      {logState.kind === "error" && <p className="text-xs text-danger" role="status" data-testid="meal-estimate-log-error">{logState.message}</p>}
      {data.variant === "items" && onSendMessage !== undefined && (
        <CardActionButton label="Save as memory" testId="meal-estimate-save-memory-btn" variant="ghost" disabled={busy} onPress={() => onSendMessage("Save this meal to my food memory")} />
      )}
    </div>
  );
}

function ItemsContent({ data }: { readonly data: MealEstimateData }) {
  const visible = data.items.slice(0, PREVIEW_ITEM_LIMIT);
  const hiddenCount = data.items.length - visible.length;
  return (
    <>
      <ul className="flex flex-col gap-1.5" data-testid="meal-estimate-items">
        {visible.map((item, index) => (
          <li key={`${item.name}-${index}`} className="flex items-baseline justify-between gap-2 text-sm" data-testid={`meal-estimate-item-${index}`}>
            <span className="min-w-0 flex-1 truncate text-text">
              {item.name}
              {item.quantity !== undefined && <span className="text-text-muted"> {item.quantity}{item.unit !== undefined ? ` ${item.unit}` : ""}</span>}
            </span>
            <span className="shrink-0 font-medium text-text">{item.calories !== undefined ? `${formatNumber(item.calories)} kcal` : "? kcal"}</span>
          </li>
        ))}
        {hiddenCount > 0 && <li className="text-xs text-text-muted">+{hiddenCount} more item{hiddenCount === 1 ? "" : "s"}</li>}
      </ul>
      <div className="rounded-xl bg-accent-light px-3 py-2" data-testid="meal-estimate-totals">
        <div className="text-xs font-semibold uppercase tracking-wide text-accent">Estimated total</div>
        <div className="text-lg font-bold leading-tight text-accent" data-testid="meal-estimate-total-kcal">{data.totals?.calories !== undefined ? `${formatNumber(data.totals.calories)} kcal` : "Some calories unknown"}</div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {data.totals?.proteinG !== undefined && <MacroChip label="Protein" value={`${formatNumber(data.totals.proteinG)} g`} />}
          {data.totals?.carbsG !== undefined && <MacroChip label="Carbs" value={`${formatNumber(data.totals.carbsG)} g`} />}
          {data.totals?.fatG !== undefined && <MacroChip label="Fat" value={`${formatNumber(data.totals.fatG)} g`} />}
          {data.totals?.fiberG !== undefined && <MacroChip label="Fibre" value={`${formatNumber(data.totals.fiberG)} g`} />}
        </div>
      </div>
      {data.uncertainty !== undefined && <p className="text-xs leading-relaxed text-text-muted" data-testid="meal-estimate-uncertainty">Range {formatNumber(data.uncertainty.lowKcal)}–{formatNumber(data.uncertainty.highKcal)} kcal{data.uncertainty.reason !== undefined ? ` — ${data.uncertainty.reason}` : ""}</p>}
    </>
  );
}

function MemoryContent({ data }: { readonly data: MealEstimateData }) {
  const match = data.memoryMatch;
  if (match === undefined) return null;
  return (
    <div className="rounded-xl bg-muted/60 px-3 py-2" data-testid="meal-estimate-memory">
      <div className="text-sm font-semibold text-text">{match.foodName}</div>
      <div className="text-xs text-text-muted">{match.servingSize !== undefined ? `${match.servingSize} · ` : ""}{formatNumber(match.calories)} kcal{match.confidence !== undefined ? ` · ${match.confidence} confidence` : ""}</div>
      {data.partialMatchCount > 0 && <div className="mt-1 text-xs text-text-muted">+{data.partialMatchCount} related memor{data.partialMatchCount === 1 ? "y" : "ies"}</div>}
    </div>
  );
}

export function MealEstimateCardView(props: MealEstimateCardViewProps) {
  const { data } = props;
  return (
    <ChatCardShell icon={Salad} title={data.variant === "items" ? data.mealName ?? "Meal estimate" : data.variant === "memory" ? "Saved value" : "Food photo"} subtitle={data.variant === "items" ? "Review the estimate before logging" : undefined} testId="chat-card-meal-estimate">
      {data.variant === "items" && <ItemsContent data={data} />}
      {data.variant === "memory" && <MemoryContent data={data} />}
      {data.variant === "image" && <p className="text-sm leading-relaxed text-text" data-testid="meal-estimate-image">{data.accessible === true ? "Your food photo is ready — BiteTrack is analysing it." : "BiteTrack couldn't access that photo. Try sending it again."}</p>}
      {data.variant !== "image" && <LogActions {...props} />}
    </ChatCardShell>
  );
}
